import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  Coins,
  Copy,
  History,
  Loader2,
  RefreshCw,
  ArrowRight,
} from 'lucide-react';
import api from 'lib/api';
import { API_ENDPOINTS } from 'constants/game';
import { TransactionRow } from 'types/game';
import PageHero from 'components/PageHero';
import { formatCurrencyAmount } from 'lib/currency-format';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';

const PAGE_SIZE = 10;

type FilterKey = 'all' | 'buy_in' | 'gameplay' | 'blind' | 'chaal' | 'show' | 'win' | 'leave';

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'buy_in', label: 'Buy In' },
  { key: 'gameplay', label: 'In-Hand' },
  { key: 'blind', label: 'Blind' },
  { key: 'chaal', label: 'Chaal' },
  { key: 'show', label: 'Show' },
  { key: 'win', label: 'Wins' },
  { key: 'leave', label: 'Cash Out' },
];

const BLIND_ACTIONS = new Set(['blind', 'blind2x', 'raise_blind', 'raise_blind2x']);
const CHAAL_ACTIONS = new Set(['chaal', 'chaal2x', 'raise_chaal', 'raise_chaal2x']);
const SHOW_ACTIONS = new Set(['show']);

const ACTION_STYLES: Record<string, { pill: string; category: FilterKey }> = {
  buy_in: { pill: 'txn-pill-buyin', category: 'buy_in' },
  leave_table: { pill: 'txn-pill-leave', category: 'leave' },
  win: { pill: 'txn-pill-win', category: 'win' },
  boot: { pill: 'txn-pill-bet', category: 'gameplay' },
  blind: { pill: 'txn-pill-bet', category: 'gameplay' },
  blind2x: { pill: 'txn-pill-raise', category: 'gameplay' },
  chaal: { pill: 'txn-pill-bet', category: 'gameplay' },
  chaal2x: { pill: 'txn-pill-raise', category: 'gameplay' },
  raise_blind: { pill: 'txn-pill-raise', category: 'gameplay' },
  raise_blind2x: { pill: 'txn-pill-raise', category: 'gameplay' },
  raise_chaal: { pill: 'txn-pill-raise', category: 'gameplay' },
  raise_chaal2x: { pill: 'txn-pill-raise', category: 'gameplay' },
  show: { pill: 'txn-pill-allin', category: 'gameplay' },
  side_show: { pill: 'txn-pill-bet', category: 'gameplay' },
};

const TYPE_STYLES: Record<number, { pill: string; category: FilterKey }> = {
  1: { pill: 'txn-pill-buyin', category: 'buy_in' },
  2: { pill: 'txn-pill-bet', category: 'gameplay' },
  3: { pill: 'txn-pill-bet', category: 'gameplay' },
  4: { pill: 'txn-pill-muted', category: 'gameplay' },
  5: { pill: 'txn-pill-raise', category: 'gameplay' },
  6: { pill: 'txn-pill-allin', category: 'gameplay' },
  7: { pill: 'txn-pill-win', category: 'win' },
  8: { pill: 'txn-pill-refund', category: 'gameplay' },
  9: { pill: 'txn-pill-leave', category: 'leave' },
  10: { pill: 'txn-pill-leave', category: 'leave' },
  11: { pill: 'txn-pill-muted', category: 'all' },
};

function rowStyle(row: TransactionRow): { pill: string; category: FilterKey } {
  if (row.action_code && ACTION_STYLES[row.action_code]) {
    return ACTION_STYLES[row.action_code];
  }
  return TYPE_STYLES[row.transaction_type] ?? { pill: 'txn-pill-muted', category: 'all' };
}

/** Maps a row to a filter bucket (action_code, type label, or legacy transaction_type). */
function resolveActionGroup(row: TransactionRow): FilterKey | 'boot' | 'side_show' | null {
  if (row.action_code) {
    if (BLIND_ACTIONS.has(row.action_code)) return 'blind';
    if (CHAAL_ACTIONS.has(row.action_code)) return 'chaal';
    if (SHOW_ACTIONS.has(row.action_code)) return 'show';
    if (row.action_code === 'buy_in') return 'buy_in';
    if (row.action_code === 'leave_table') return 'leave';
    if (row.action_code === 'win') return 'win';
    if (row.action_code === 'boot') return 'boot';
    if (row.action_code === 'side_show') return 'side_show';
  }

  const label = row.typeLabel.trim().toLowerCase();
  if (label.includes('blind')) return 'blind';
  if (label.includes('chaal') || label.includes('chal')) return 'chaal';
  if (label === 'show') return 'show';
  if (label === 'buy in') return 'buy_in';
  if (label === 'win') return 'win';
  if (label === 'cash out') return 'leave';

  const type = row.transaction_type;
  if (type === 1) return 'buy_in';
  if (type === 7) return 'win';
  if (type === 9 || type === 10) return 'leave';
  if (type === 6) return 'show';
  if (type === 3) return 'chaal';
  if (type === 2) return 'blind';
  if ([4, 5, 8].includes(type)) return 'gameplay';
  return null;
}

function matchesFilter(row: TransactionRow, filter: FilterKey): boolean {
  if (filter === 'all') return true;

  const group = resolveActionGroup(row);

  if (filter === 'blind' || filter === 'chaal' || filter === 'show') {
    return group === filter;
  }

  if (filter === 'buy_in' || filter === 'win' || filter === 'leave') {
    return group === filter || rowStyle(row).category === filter;
  }

  if (filter === 'gameplay') {
    if (group === 'blind' || group === 'chaal' || group === 'show') return false;
    return group === 'boot' || group === 'side_show' || rowStyle(row).category === 'gameplay';
  }

  return false;
}

function txnDelta(row: TransactionRow): number {
  return Number(row.balance_after) - Number(row.balance_before);
}

function formatMoney(symbol: string, amount: number, currencyCode?: string | null): string {
  return formatCurrencyAmount(symbol, Math.abs(Number(amount) || 0), currencyCode, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function formatSignedAmount(symbol: string, row: TransactionRow): string {
  const delta = txnDelta(row);
  const formatted = formatMoney(symbol, row.amount, row.currency_code);
  if (delta > 0) return `+${formatted}`;
  if (delta < 0) return `−${formatted}`;
  return formatted;
}

function formatDate(value: string): string {
  const d = new Date(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).replace(',', '');
}

function formatMobileDate(value: string): string {
  const d = new Date(value);
  const day = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const time = d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${day}, ${time}`;
}

/** Shorter ID for narrow screens — full value stays on tap/copy. */
function formatTxnIdMobile(id: string | null): string {
  if (!id?.trim()) return '—';
  const normalized = id.trim().toLowerCase();
  if (normalized.length <= 16) return normalized;
  return `${normalized.slice(0, 8)}…${normalized.slice(-4)}`;
}

function MobileTxnFooter({ id, date }: { id: string | null; date: string }) {
  const [copied, setCopied] = useState(false);
  const full = fullTxnId(id);
  const preview = formatTxnIdMobile(id);
  const canCopy = full !== '—';

  const copyId = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="txn-mobile-footer-row">
      <span className="txn-mobile-footer-date">{date}</span>
      {canCopy ? (
        <button
          type="button"
          className="txn-mobile-id-btn"
          onClick={() => void copyId()}
          title={full}
          aria-label={copied ? 'Copied transaction ID' : 'Copy transaction ID'}
        >
          <span className="txn-mobile-id-label">ID</span>
          <span className="txn-mobile-id-value">{preview}</span>
          {copied ? (
            <Check size={12} strokeWidth={2.5} className="text-emerald-400" />
          ) : (
            <Copy size={12} strokeWidth={2} />
          )}
        </button>
      ) : (
        <span className="txn-mobile-id-value">{preview}</span>
      )}
    </div>
  );
}

function fullTxnId(id: string | null): string {
  return id?.trim() || '—';
}

/** e.g. 310f4ab3-7b2c... */
function formatTxnIdPreview(id: string | null): string {
  if (!id?.trim()) return '—';
  const normalized = id.trim().toLowerCase();
  const parts = normalized.split('-');
  if (parts.length >= 2) {
    return `${parts[0]}-${parts[1]}...`;
  }
  return normalized.length > 13 ? `${normalized.slice(0, 13)}...` : normalized;
}

function TxnIdCell({ id, compact = false }: { id: string | null; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  const full = fullTxnId(id);
  const preview = formatTxnIdPreview(id);
  const canCopy = full !== '—';

  const copyId = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className={compact ? 'txn-id-cell txn-id-cell-compact' : 'txn-id-cell'}>
      <span className="txn-cell txn-cell-txid" title={full}>
        {preview}
      </span>
      {canCopy && (
        <button
          type="button"
          className="txn-copy-btn"
          onClick={() => void copyId()}
          title={copied ? 'Copied' : 'Copy transaction ID'}
          aria-label={copied ? 'Copied transaction ID' : 'Copy transaction ID'}
        >
          {copied ? <Check size={15} strokeWidth={2.5} /> : <Copy size={15} strokeWidth={2} />}
        </button>
      )}
    </div>
  );
}

function tableName(row: TransactionRow): string {
  return row.table_name?.trim() || '—';
}

function balanceScopeLabel(scope: TransactionRow['balance_scope']): string {
  return scope === 'wallet' ? 'Main wallet' : 'Table stack';
}

export default function Transactions() {
  const [rows, setRows] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const loadingMoreRef = useRef(false);

  const fetchPage = useCallback(async (cursor?: number, append = false) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
    if (cursor != null) params.set('cursor', String(cursor));
    const path = `${API_ENDPOINTS.FRONTEND.WALLET_TRANSACTIONS}?${params.toString()}`;

    const res = await api.get<TransactionRow[]>(path);
    if (res.success && Array.isArray(res.data)) {
      setRows((prev) => (append ? [...prev, ...res.data!] : res.data!));
      setNextCursor(res.pagination?.nextCursor ?? null);
      setHasMore(Boolean(res.pagination?.hasMore));
    }
  }, []);

  useEffect(() => {
    fetchPage().finally(() => setLoading(false));
  }, [fetchPage]);

  const refresh = async () => {
    setRefreshing(true);
    await fetchPage();
    setRefreshing(false);
  };

  const loadMore = useCallback(async () => {
    if (!hasMore || nextCursor == null || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      await fetchPage(nextCursor, true);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [fetchPage, hasMore, nextCursor]);

  const sentinelRef = useInfiniteScroll(loadMore, hasMore && !loading && !loadingMore, [
    hasMore,
    loading,
    loadingMore,
    nextCursor,
    rows.length,
  ]);

  const filteredRows = useMemo(
    () => rows.filter((row) => matchesFilter(row, filter)),
    [rows, filter]
  );

  return (
    <div className="txn-page pb-8">
      <PageHero
        eyebrow="Wallet ledger"
        title="Transaction History"
        showBack
        description="Buy-in and cash-out show main wallet; in-hand actions show your table stack."
        descriptionClassName="hidden sm:block"
        icon={<History size={16} className="text-tp-gold" />}
        aside={
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing || loading}
            className="txn-refresh-btn w-full sm:w-auto"
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        }
      />

      <div className="txn-panel">
        <div className="txn-panel-toolbar">
          <div className="txn-filter-row txn-filter-row-mobile">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setFilter(opt.key)}
                className={`txn-filter-btn ${filter === opt.key ? 'txn-filter-btn-active' : ''}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="txn-empty">
            <Loader2 className="animate-spin text-tp-gold" size={28} />
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="txn-empty">
            <Coins className="text-white/20" size={36} />
            <p className="mt-3 font-medium text-white/75">
              {rows.length === 0 ? 'No transactions yet' : 'No entries for this filter'}
            </p>
            <p className="mt-1 text-sm text-white/45">
              Join a table to see buy-ins, in-hand bets, wins, and leave cash-outs.
            </p>
          </div>
        ) : (
          <>
            <div className="txn-table-wrap hidden lg:block">
              <table className="txn-table">
                <colgroup>
                  <col className="txn-col-txid" />
                  <col className="txn-col-type" />
                  <col className="txn-col-table" />
                  <col className="txn-col-amount" />
                  <col className="txn-col-before" />
                  <col className="txn-col-close" />
                  <col className="txn-col-date" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Type</th>
                    <th>Table</th>
                    <th className="txn-th-money">Amount</th>
                    <th className="txn-th-money">Before Balance</th>
                    <th className="txn-th-money">Close Balance</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const style = rowStyle(row);
                    const delta = txnDelta(row);
                    const amountClass =
                      delta > 0 ? 'txn-amount-up' : delta < 0 ? 'txn-amount-down' : 'txn-amount-flat';
                    const date = formatDate(row.created_at);
                    const scopeHint = balanceScopeLabel(row.balance_scope);

                    return (
                      <tr key={row.id}>
                        <td>
                          <TxnIdCell id={row.transaction_id} />
                        </td>
                        <td>
                          <span className={`txn-pill ${style.pill}`} title={row.typeLabel}>
                            {row.typeLabel}
                          </span>
                        </td>
                        <td>
                          <span className="txn-cell txn-cell-table" title={tableName(row)}>
                            {tableName(row)}
                          </span>
                        </td>
                        <td className="txn-td-money">
                          <span className={`txn-cell txn-cell-amount ${amountClass}`}>
                            {formatSignedAmount(row.currency_symbol, row)}
                          </span>
                        </td>
                        <td className="txn-td-money">
                          <span
                            className="txn-cell txn-cell-balance"
                            title={`${scopeHint}: ${formatMoney(row.currency_symbol, row.balance_before)}`}
                          >
                            {formatMoney(row.currency_symbol, row.balance_before)}
                          </span>
                        </td>
                        <td className="txn-td-money">
                          <span
                            className="txn-cell txn-cell-balance txn-cell-balance-close"
                            title={`${scopeHint}: ${formatMoney(row.currency_symbol, row.balance_after)}`}
                          >
                            {formatMoney(row.currency_symbol, row.balance_after)}
                          </span>
                        </td>
                        <td className="txn-td-date">
                          <span className="txn-cell txn-cell-date" title={date}>
                            {date}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="txn-mobile-list lg:hidden">
              {filteredRows.map((row) => {
                const style = rowStyle(row);
                const delta = txnDelta(row);
                const amountClass =
                  delta > 0 ? 'txn-amount-up' : delta < 0 ? 'txn-amount-down' : 'txn-amount-flat';
                const date = formatMobileDate(row.created_at);
                const scopeHint = balanceScopeLabel(row.balance_scope);

                return (
                  <article key={row.id} className="txn-mobile-card">
                    <div className="txn-mobile-header">
                      <span className={`txn-pill ${style.pill}`} title={row.typeLabel}>
                        {row.typeLabel}
                      </span>
                      <span className={`txn-mobile-amount ${amountClass}`}>
                        {formatSignedAmount(row.currency_symbol, row)}
                      </span>
                    </div>

                    <div className="txn-mobile-sub">
                      <span className="txn-mobile-table">{tableName(row)}</span>
                      <span className="txn-mobile-scope-badge">{scopeHint}</span>
                    </div>

                    <div className="txn-mobile-balance-row">
                      <div className="txn-mobile-balance-item">
                        <span className="txn-mobile-balance-label">Before</span>
                        <span
                          className="txn-mobile-balance-value"
                          title={formatMoney(row.currency_symbol, row.balance_before)}
                        >
                          {formatMoney(row.currency_symbol, row.balance_before)}
                        </span>
                      </div>
                      <ArrowRight size={14} className="txn-mobile-balance-arrow" aria-hidden />
                      <div className="txn-mobile-balance-item txn-mobile-balance-item-close">
                        <span className="txn-mobile-balance-label">Close</span>
                        <span
                          className="txn-mobile-balance-value"
                          title={formatMoney(row.currency_symbol, row.balance_after)}
                        >
                          {formatMoney(row.currency_symbol, row.balance_after)}
                        </span>
                      </div>
                    </div>

                    <MobileTxnFooter id={row.transaction_id} date={date} />
                  </article>
                );
              })}
            </div>
          </>
        )}

        {!loading && hasMore && (
          <div ref={sentinelRef} className="txn-load-sentinel">
            {loadingMore && (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-white/45">
                <Loader2 size={16} className="animate-spin text-tp-gold" />
                Loading more…
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
